import { api } from "../index";

const endpoint = "/api/stockmoves";

export interface StockMoveQuery {
  companyId?: number;
  productId?: number;
  warehouseId?: number;
  startDate?: string;
  endDate?: string;
  moveTypeId?: number;
  sourceTypeId?: number;
  sourceId?: number;
  /** Substring match (case-insensitive) on linked purchase order code for PO-sourced moves */
  purchaseOrderCode?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface StockMoveDto {
  id: number;
  companyId: number;
  productId: number;
  warehouseId: number;
  moveDate: string;
  direction: number;
  quantity: number;
  unitCost?: number;
  currencyId: number;
  moveTypeId: number;
  sourceTypeId?: number;
  sourceId?: number;
  createdAt: string;
  createdBy: string;
  company?: any;
  product?: any;
  warehouse?: any;
  currency?: any;
  moveType?: any;
  sourceType?: any;
  purchaseOrderCode?: string;
  receiptAcknowledgements?: { code: string; invoiceNo: string }[];
  hasSyntheticOutbound?: boolean;
}

export interface CreateStockMoveDto {
  companyId: number;
  productId: number;
  warehouseId: number;
  moveDate: string;
  direction: number;
  quantity: number;
  unitCost?: number;
  currencyId: number;
  moveTypeId: number;
  sourceTypeId?: number;
  sourceId?: number;
}

export interface UpdateStockMoveDto {
  unitCost?: number;
  quantity?: number;
  currencyId?: number;
  productId?: number;
  sourceId?: number;
  sourceTypeId?: number;
  moveDate?: string;
}

export interface ManualPriceUpdateDto {
  inboundId: number;
  productId: number;
  unitCost: number;
  currencyId: number;
}

export interface KardexDto {
  productId: number;
  warehouseId: number;
  moveDate: string;
  moveType: string;
  direction: number;
  quantity: number;
  unitCost?: number;
  runningBalance: number;
  sourceReference?: string;
}

export const getStockMoveList = async (query: StockMoveQuery): Promise<StockMoveDto[]> => {
  const response = await api.get(endpoint, { params: query });
  return response.data;
};

export const getStockMoveById = async (id: number): Promise<StockMoveDto> => {
  const response = await api.get(`${endpoint}/${id}`);
  return response.data;
};

export const createStockMove = async (data: CreateStockMoveDto): Promise<StockMoveDto> => {
  const response = await api.post(endpoint, data);
  return response.data;
};

export const updateStockMove = async (id: number, data: UpdateStockMoveDto): Promise<StockMoveDto> => {
  const response = await api.put(`${endpoint}/${id}`, data);
  return response.data;
};

export const deleteStockMove = async (id: number): Promise<void> => {
  await api.delete(`${endpoint}/${id}`);
};

export const getKardex = async (
  productId: number,
  warehouseId: number,
  startDate?: string,
  endDate?: string
): Promise<KardexDto[]> => {
  const response = await api.get(`${endpoint}/kardex`, {
    params: { productId, warehouseId, startDate, endDate }
  });
  return response.data;
};

export const updateInboundPrice = async (data: ManualPriceUpdateDto): Promise<void> => {
  await api.post(`${endpoint}/update-inbound-price`, data);
};

export interface StockDto {
  productId: number;
  productCode: string;
  productDescription?: string;
  stockQuantity: number;
}

export const getStock = async (warehouseId: number): Promise<StockDto[]> => {
  const response = await api.get(`${endpoint}/stock/${warehouseId}`);
  return response.data;
};

export interface SyntheticInventoryReportRow {
  productId: number;
  productCode: string;
  initialStock: number;
  newInbounds: number;
  newOutbounds: number;
  newStock: number;
  newUnitPrice?: number | null;
  value?: number | null;
}

export const getSyntheticInventoryReport = async (query: {
  companyId: number;
  startDate: string;
  endDate: string;
  productId?: number;
}): Promise<SyntheticInventoryReportRow[]> => {
  const response = await api.get(`${endpoint}/synthetic-inventory-report`, { params: query });
  return response.data;
};